/* eslint-disable no-unused-vars */

import {
  call,
  put,
  takeEvery,
  // fork,
  all,
} from 'redux-saga/effects';

import API from '../../api';
import { ProjectsActionTypes } from '../actions/types';
import {
  ProjectsActionCreators,
  PackagesActionCreators,
  UsersActionCreators,
  ModalsActionCreators,
  TableActionCreators,
} from '../actions/creators';
import { AsyncActionStatus } from '../enums';
import { generateErrorMessage, generateMessageWithLinks } from '../helpers';
import { ROUTES } from '../../router';
import Mixpanel from '../../../helpers/mixpanel';
import { ProjectTypes } from '../../../../shared/enums';
import { PAGINATION } from '../../../../shared/constants';
import { Table } from '../../views/components';
import allProjectsTableAPI from '../../views/pages/AllProjects/allProjectsTableAPI';
import { userRoleCheckers } from '../../../../shared/helpers';
import { tableXlsxExport, toast } from '../../utilities';
// import { fetchPackageByProjectId } from './packages.saga';

function* fetchProjectById(action) {
  try {
    const round = number => Math.round(number * 100) / 100;
    const start = performance.now();
    const response = yield call(API.Projects.getProjectById, action.payload.projectId, action.payload.specifiedProjectFields);
    yield put(ProjectsActionCreators.fetchProjectById[AsyncActionStatus.FULFILLED](
      response.data.project,
    ));
    const end = performance.now();
    const diff = round(end - start);
    console.log('[Project API] Action process time:', diff, 'ms');
  } catch (error) {
    yield put(ProjectsActionCreators.fetchProjectById[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* createProject(action) {
  try {
    const response = yield call(
      API.Projects.createProject,
      action.payload.project,
      action.payload.customer,
      action.payload.writer,
      action.payload.editor,
      action.payload.developmentalEditor,
      action.payload.proProofreader,
      action.payload.contentTeam,
      action.payload.package,
      action.payload.timelineSchedule,
      action.payload.extensionFeeDeadline,
      action.payload.isProjectTimelineShown,
      action.payload.isProjectAccessBlocked,
      action.payload.isEmailShouldBeSentOnBlock,
      action.payload.salesOwner,
      action.payload.customUpgradeRequest,
      action.payload.printerType,
    );
    yield put(ProjectsActionCreators.createProject[AsyncActionStatus.FULFILLED](response.data.project));
    yield put(ModalsActionCreators.openProjectCreated(
      { projectId: response.data.project._id },
    ));
    if (!action.payload.customer._id) {
      Mixpanel.track('New Customer created', { projectType: ProjectTypes.CLASSIC });
    }
    if (response.data.hubspotError) yield call(toast.error, response.data.hubspotError);
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(ProjectsActionCreators.createProject[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* editProjectWithRelatedData(action) {
  try {
    const response = yield call(
      API.Projects.editProjectWithRelatedData,
      action.payload.customer,
      action.payload.writer,
      action.payload.editor,
      action.payload.developmentalEditor,
      action.payload.proProofreader,
      action.payload.contentTeam,
      action.payload.package,
      action.payload.project,
      action.payload.timelineSchedule,
      action.payload.extensionFeeDeadline,
      action.payload.isProjectTimelineShown,
      action.payload.isProjectAccessBlocked,
      action.payload.isEmailShouldBeSentOnBlock,
      action.payload.salesOwner,
      action.payload.customUpgradeRequest,
      action.payload.printerType,
    );
    yield put(ProjectsActionCreators.editProjectWithRelatedData[AsyncActionStatus.FULFILLED](
      response.data.customer,
      response.data.writer,
      response.data.editor,
      response.data.developmentalEditor,
      response.data.proProofreader,
      response.data.contentTeam,
      response.data.package,
      response.data.project,
    ));
    yield put(ModalsActionCreators.closeUseTimelineModal());
    yield call(action.payload.navigate, ROUTES.projects.overview.byProjectId.url(response.data.project._id));
    yield call(toast.success, t('toastMessages.success.projectUpdatedWithRelatedData', { ns: i18next.Namespace.COMMON }));
    if (response.data.hubspotError) yield call(toast.error, response.data.hubspotError);
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(ProjectsActionCreators.editProjectWithRelatedData[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* deleteProjectById(action) {
  try {
    const response = yield call(API.Projects.deleteProjectById, action.payload.projectId);
    yield put(ProjectsActionCreators.deleteProjectById[AsyncActionStatus.FULFILLED](
      response.data.project,
    ));
    yield call(action.payload.navigate, ROUTES.projects.list.general.url);
    yield call(toast.success, t('toastMessages.success.projectDeleted', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const errMsg = generateErrorMessage(error);
    yield put(ProjectsActionCreators.deleteProjectById[AsyncActionStatus.REJECTED](errMsg));
    yield call(toast.error, errMsg);
  }
}

function* toggleProjectArchivingById(action) {
  try {
    const { projectId, isProjectCompleted } = action.payload;
    const response = yield call(API.Projects.toggleProjectArchivingById, projectId, isProjectCompleted);
    const { project } = response.data;
    yield put(ProjectsActionCreators.toggleProjectArchivingById[AsyncActionStatus.FULFILLED](project));
    yield call(
      toast.success,
      project.isArchived && isProjectCompleted
        ? t('toastMessages.success.projectCompleted', { ns: i18next.Namespace.COMMON })
        : project.isArchived && !isProjectCompleted
          ? t('toastMessages.success.projectArchived', { ns: i18next.Namespace.COMMON }) : t('toastMessages.success.projectUnarchived', { ns: i18next.Namespace.COMMON }),
    );
  } catch (error) {
    const errMsg = generateErrorMessage(error);
    yield put(ProjectsActionCreators.toggleProjectArchivingById[AsyncActionStatus.REJECTED](errMsg));
    yield call(toast.error, errMsg);
  }
}

function* fetchChaptersAndResourcesByProjectId(action) {
  try {
    const response = yield call(
      API.Projects.getChaptersAndResourcesByProjectId,
      action.payload.projectId,
      action.payload.permissions,
    );
    yield put(ProjectsActionCreators.fetchChaptersAndResourcesByProjectId[AsyncActionStatus.FULFILLED](
      response.data.chapters,
      response.data.book,
      response.data.project,
      response.data.packageDoc,
      response.data.interviews,
    ));
  } catch (error) {
    yield put(ProjectsActionCreators.fetchChaptersAndResourcesByProjectId[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* getBookSampleAndResourcesByProjectId(action) {
  try {
    const response = yield call(
      API.Projects.getBookSampleAndResourcesByProjectId,
      action.payload.projectId,
    );
    yield put(ProjectsActionCreators.getBookSampleAndResourcesByProjectId[AsyncActionStatus.FULFILLED](
      response.data.book,
      response.data.project,
      response.data.packageDoc,
      response.data.interviews,
    ));
  } catch (error) {
    yield put(ProjectsActionCreators.getBookSampleAndResourcesByProjectId[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

export function* fetchDataToCreateProject(action) {
  try {
    const response = yield call(API.Projects.getDataToCreateProject);
    yield put(ProjectsActionCreators.fetchDataToCreateProject[AsyncActionStatus.FULFILLED](
      response.data.users,
      response.data.packages,
      response.data.prices,
    ));
    yield put(ModalsActionCreators.openHubspotModal());
  } catch (error) {
    yield put(ProjectsActionCreators.fetchDataToCreateProject[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* fetchChaptersAndResourcesByUser(action) {
  try {
    const response = yield call(
      API.Projects.getChaptersAndResourcesByUser,
      action.payload.permissions,
    );
    yield put(ProjectsActionCreators.fetchChaptersAndResourcesByUser[AsyncActionStatus.FULFILLED](
      response.data.chapters,
      response.data.book,
      response.data.project,
      response.data.packageDoc,
    ));
  } catch (error) {
    yield put(ProjectsActionCreators.fetchChaptersAndResourcesByUser[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* getBookSampleAndResourcesByUser(action) {
  try {
    const response = yield call(
      API.Projects.getBookSampleAndResourcesByUser,
    );
    yield put(ProjectsActionCreators.getBookSampleAndResourcesByUser[AsyncActionStatus.FULFILLED](
      response.data.book,
      response.data.project,
      response.data.packageDoc,
    ));
  } catch (error) {
    yield put(ProjectsActionCreators.getBookSampleAndResourcesByUser[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

export function* fetchDataToEditProject(action) {
  try {
    const response = yield call(
      API.Projects.getDataToEditProject,
      action.payload.projectId,
    );
    yield put(ProjectsActionCreators.fetchDataToEditProject[AsyncActionStatus.FULFILLED](
      response.data.project,
      response.data.users,
      response.data.packages,
      response.data.prices,
      response.data.book,
    ));
  } catch (error) {
    yield put(ProjectsActionCreators.fetchDataToEditProject[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* getDataForProjectsList(action) {
  try {
    const { filters, pagination } = action.payload;
    const response = yield call(
      API.Projects.getDataForProjectsList,
      action.payload.filters,
      action.payload.pagination,
      action.payload.sortBy,
    );

    if ([ROUTES.projects.list.general.path, ROUTES.projects.list.writer.path].includes(window.location.pathname)) {
      yield put(ProjectsActionCreators.getDataForProjectsList[AsyncActionStatus.FULFILLED](response.data.projects));
      yield put(TableActionCreators.setPagination({
        pageIndex: response.data.pageIndex,
        pageSize: response.data.pageSize,
        pageCount: response.data.pageCount,
        rowsTotalCount: response.data.rowsTotalCount,
      }));
      yield put(TableActionCreators.setSortBy(
        Table.convertSortByForTable(response.data.sortBy, allProjectsTableAPI.columnsFixtures),
      ));
    } else {
      yield put(ProjectsActionCreators.getDataForProjectsList[AsyncActionStatus.REJECTED]());
    }
  } catch (error) {
    yield put(ProjectsActionCreators.getDataForProjectsList[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* fetchProjectOverviewDataByProjectId(action) {
  try {
    const response = yield call(
      API.Projects.getProjectOverviewDataByProjectId,
      action.payload.projectId,
    );
    yield put(ModalsActionCreators.closeRedirectToRateWriterModal());

    yield put(ProjectsActionCreators.fetchProjectOverviewDataByProjectId[AsyncActionStatus.FULFILLED](
      response.data.project,
      response.data.members,
      response.data.package,
      response.data.book,
      response.data.interviews,
    ));
    if (response.data.project.isPackageUpgradeRequested) yield put(ModalsActionCreators.openUpgradeRequestedModal());

    const isProjectWithWriter = response.data.members.some(({ role }) => userRoleCheckers.isWriter(role));
    if (!response.data.isWriterWasRated && isProjectWithWriter) {
      yield put(ModalsActionCreators.openRedirectToRateWriterModal());
    }
  } catch (err) {
    yield call(action.payload.navigate, ROUTES.noProjectForCustomer, { replace: true });
    yield put(ProjectsActionCreators.fetchProjectOverviewDataByProjectId[AsyncActionStatus.REJECTED](
      generateErrorMessage(err),
    ));
  }
}

function* fetchProjectOverviewDataByCustomerId(action) {
  try {
    const response = yield call(
      API.Projects.getProjectOverviewDataByCustomerId,
      action.payload.customerId,
    );

    yield put(ProjectsActionCreators.fetchProjectOverviewDataByCustomerId[AsyncActionStatus.FULFILLED](
      response.data.project,
      response.data.members,
      response.data.package,
      response.data.book,
      response.data.interviews,
    ));
    if (response.data.book.shouldShowOnbordingFeedback) yield put(ModalsActionCreators.openOnboardingFeedbackModal());
  } catch (err) {
    yield call(action.payload.navigate, ROUTES.noProjectForCustomer, { replace: true });
    yield put(ProjectsActionCreators.fetchProjectOverviewDataByCustomerId[AsyncActionStatus.REJECTED](
      generateErrorMessage(err),
    ));
  }
}

function* addWriterToProject(action) {
  try {
    const response = yield call(
      API.Projects.addWriterToProject,
      action.payload.projectId,
      action.payload.writerId,
      action.payload.writerLevelForProject,
    );
    Mixpanel.track('Writer match');
    yield put(ProjectsActionCreators.addWriterToProject[AsyncActionStatus.FULFILLED](
      response.data.project,
    ));
    yield put(ModalsActionCreators.openWriterConfirmedOnProject({ writerId: response.data.project.writerId }));
    yield call(toast.success, t('toastMessages.success.writerMatched', { ns: i18next.Namespace.COMMON }));
  } catch (err) {
    const message = generateErrorMessage(err);
    yield put(ProjectsActionCreators.addWriterToProject[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* moveToNextStageOfProject(action) {
  try {
    const response = yield call(
      API.Projects.moveToNextStageOfProject,
      action.payload.projectId,
      action.payload.isEmailsIncluded,
      action.payload.bookFieldsToUpdate,
    );
    yield put(ProjectsActionCreators.moveToNextStageOfProject[AsyncActionStatus.FULFILLED](response.data.project));
  } catch (err) {
    const message = generateErrorMessage(err);
    yield put(ProjectsActionCreators.moveToNextStageOfProject[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* fetchTimelineDataByCustomer(action) {
  try {
    const response = yield call(
      API.Projects.getTimelineDataForCustomer,
      action.payload.customerId,
    );

    yield put(ProjectsActionCreators.fetchTimelineDataByCustomer[AsyncActionStatus.FULFILLED](
      response.data.project,
      response.data.package,
      response.data.members,
      response.data.book,
    ));
    if (response.data.book.shouldShowOnbordingFeedback) yield put(ModalsActionCreators.openOnboardingFeedbackModal());
  } catch (err) {
    yield put(ProjectsActionCreators.fetchTimelineDataByCustomer[AsyncActionStatus.REJECTED](
      generateErrorMessage(err),
    ));
  }
}

function* toggleUserEnableToEditDraft(action) {
  try {
    const { projectId, userRole } = action.payload;
    const response = yield call(
      API.Projects.toggleUserEnableToEditDraft,
      projectId,
      userRole,
    );

    yield put(ProjectsActionCreators.toggleUserEnableToEditDraft[AsyncActionStatus.FULFILLED](
      response.data.project,
    ));

    const toastMessages = response.data.project[`is${userRole}EnableToEditDraft`]
      ? t(`toastMessages.success.accessGrantedFor${userRole}`, { ns: i18next.Namespace.COMMON, page: 'Draft' })
      : t(`toastMessages.success.accessRemovedFor${userRole}`, { ns: i18next.Namespace.COMMON, page: 'Draft' });
    yield call(toast.success, toastMessages);
  } catch (err) {
    const message = generateErrorMessage(err);
    yield put(ProjectsActionCreators.toggleUserEnableToEditDraft[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* toggleUserEnableToEditOutline(action) {
  try {
    const { projectId, userRole } = action.payload;
    const response = yield call(
      API.Projects.toggleUserEnableToEditOutline,
      projectId,
      userRole,
    );

    yield put(ProjectsActionCreators.toggleUserEnableToEditOutline[AsyncActionStatus.FULFILLED](
      response.data.project,
    ));

    const toastMessages = response.data.project[`is${userRole}EnableToEditOutline`]
      ? t(`toastMessages.success.accessGrantedFor${userRole}`, { ns: i18next.Namespace.COMMON, page: 'Outline' })
      : t(`toastMessages.success.accessRemovedFor${userRole}`, { ns: i18next.Namespace.COMMON, page: 'Outline' });
    yield call(toast.success, toastMessages);
  } catch (err) {
    const message = generateErrorMessage(err);
    yield put(ProjectsActionCreators.toggleUserEnableToEditOutline[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* toggleUserEnableToDownloadOutline(action) {
  try {
    const { projectId, userRole } = action.payload;
    const response = yield call(
      API.Projects.toggleUserEnableToDownloadOutline,
      projectId,
      userRole,
    );

    yield put(ProjectsActionCreators.toggleUserEnableToDownloadOutline[AsyncActionStatus.FULFILLED](
      response.data.project,
    ));

    const toastMessages = response.data.project[`is${userRole}EnableToDownloadOutline`]
      ? t(`toastMessages.success.accessDownloadGrantedFor${userRole}`, { ns: i18next.Namespace.COMMON, page: 'Outline' })
      : t(`toastMessages.success.accessDownloadRemovedFor${userRole}`, { ns: i18next.Namespace.COMMON, page: 'Outline' });
    yield call(toast.success, toastMessages);
  } catch (err) {
    const message = generateErrorMessage(err);
    yield put(ProjectsActionCreators.toggleUserEnableToDownloadOutline[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* getProjectsDataForExcel(action) {
  try {
    const { filters, sortBy } = action.payload;
    const response = yield call(
      API.Projects.getProjectsDataForExcel,
      filters,
      sortBy,
    );
    tableXlsxExport(response.data.projects, 'Projects', allProjectsTableAPI);
    yield put(ProjectsActionCreators.getProjectsDataForExcel[AsyncActionStatus.FULFILLED]());
  } catch (error) {
    yield put(ProjectsActionCreators.getProjectsDataForExcel[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}
function* customerReviewedUpgrades() {
  try {
    yield call(API.Projects.customerReviewedUpgrades);
    yield put(ProjectsActionCreators.customerReviewedUpgrades[AsyncActionStatus.FULFILLED]());
  } catch (error) {
    yield put(ProjectsActionCreators.customerReviewedUpgrades[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* getDataFromHubspot(action) {
  try {
    const response = yield call(API.Projects.getDataFromHubspot, action.payload.hubspotDealID);
    yield put(ProjectsActionCreators.getDataFromHubspot[AsyncActionStatus.FULFILLED](
      response.data.users,
    ));
    if (response.data.warningMessage) {
      yield call(toast.warning, generateMessageWithLinks(response.data.warningMessage), true);
    }
    yield put(ModalsActionCreators.closeHubspotModal());
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(ProjectsActionCreators.getDataFromHubspot[AsyncActionStatus.REJECTED](
      generateErrorMessage(message),
    ));
    yield call(toast.error, message);
  }
}

function* editorSeenRequestForUpgrade(action) {
  try {
    const response = yield call(API.Projects.editorSeenRequestForUpgrade, action.payload.projectId);
    yield put(ModalsActionCreators.closeUpgradeRequestedModal());
    yield put(ProjectsActionCreators.editorSeenRequestForUpgrade[AsyncActionStatus.FULFILLED](response.data));
  } catch (error) {
    const errMsg = generateErrorMessage(error);
    yield put(ProjectsActionCreators.editorSeenRequestForUpgrade[AsyncActionStatus.REJECTED](errMsg));
    yield call(toast.error, errMsg);
  }
}

function* getProjectAndBookData(action) {
  try {
    const response = yield call(
      API.Projects.getProjectAndBookData,
      action.payload.projectId,
    );
    yield put(ProjectsActionCreators.getProjectAndBookData[AsyncActionStatus.FULFILLED](
      response.data.book,
      response.data.project,
      response.data.interviews,
    ));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(ProjectsActionCreators.getProjectAndBookData[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* cancelUpgradeRequest(action) {
  try {
    const response = yield call(
      API.Projects.cancelUpgradeRequest,
      action.payload.projectId,
      action.payload.upgradeRequestId,
    );
    yield put(ProjectsActionCreators.cancelUpgradeRequest[AsyncActionStatus.FULFILLED](
      response.data.project,
    ));
    yield call(toast.success, t('toastMessages.success.upgradeRequestRejected', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(ProjectsActionCreators.cancelUpgradeRequest[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* completeUpgradeRequest(action) {
  try {
    const response = yield call(
      API.Projects.completeUpgradeRequest,
      action.payload.projectId,
      action.payload.upgradeRequestId,
    );
    yield put(ProjectsActionCreators.completeUpgradeRequest[AsyncActionStatus.FULFILLED](
      response.data.project,
      response.data.package,
    ));
    yield call(toast.success, t('toastMessages.success.upgradeRequestCompleted', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(ProjectsActionCreators.completeUpgradeRequest[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* getNextStageForProject(action) {
  try {
    const response = yield call(
      API.Projects.getNextStageForProject,
      action.payload.projectId,
    );
    yield put(ProjectsActionCreators.getNextStageForProject[AsyncActionStatus.FULFILLED](response.data.project));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(ProjectsActionCreators.getNextStageForProject[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

export default [
  takeEvery(
    ProjectsActionTypes.fetchProjectOverviewDataByProjectId[AsyncActionStatus.PENDING],
    fetchProjectOverviewDataByProjectId,
  ),
  takeEvery(
    ProjectsActionTypes.fetchProjectOverviewDataByCustomerId[AsyncActionStatus.PENDING],
    fetchProjectOverviewDataByCustomerId,
  ),
  takeEvery(
    ProjectsActionTypes.getDataForProjectsList[AsyncActionStatus.PENDING],
    getDataForProjectsList,
  ),
  takeEvery(
    ProjectsActionTypes.fetchProjectById[AsyncActionStatus.PENDING],
    fetchProjectById,
  ),
  takeEvery(
    ProjectsActionTypes.createProject[AsyncActionStatus.PENDING],
    createProject,
  ),
  takeEvery(
    ProjectsActionTypes.editProjectWithRelatedData[AsyncActionStatus.PENDING],
    editProjectWithRelatedData,
  ),
  takeEvery(
    ProjectsActionTypes.deleteProjectById[AsyncActionStatus.PENDING],
    deleteProjectById,
  ),
  takeEvery(
    ProjectsActionTypes.toggleProjectArchivingById[AsyncActionStatus.PENDING],
    toggleProjectArchivingById,
  ),
  takeEvery(
    ProjectsActionTypes.fetchChaptersAndResourcesByProjectId[AsyncActionStatus.PENDING],
    fetchChaptersAndResourcesByProjectId,
  ),
  takeEvery(
    ProjectsActionTypes.fetchChaptersAndResourcesByUser[AsyncActionStatus.PENDING],
    fetchChaptersAndResourcesByUser,
  ),
  takeEvery(
    ProjectsActionTypes.fetchDataToCreateProject[AsyncActionStatus.PENDING],
    fetchDataToCreateProject,
  ),
  takeEvery(
    ProjectsActionTypes.fetchDataToEditProject[AsyncActionStatus.PENDING],
    fetchDataToEditProject,
  ),
  takeEvery(
    ProjectsActionTypes.addWriterToProject[AsyncActionStatus.PENDING],
    addWriterToProject,
  ),
  takeEvery(
    ProjectsActionTypes.moveToNextStageOfProject[AsyncActionStatus.PENDING],
    moveToNextStageOfProject,
  ),
  takeEvery(
    ProjectsActionTypes.fetchTimelineDataByCustomer[AsyncActionStatus.PENDING],
    fetchTimelineDataByCustomer,
  ),
  takeEvery(
    ProjectsActionTypes.toggleUserEnableToEditDraft[AsyncActionStatus.PENDING],
    toggleUserEnableToEditDraft,
  ),
  takeEvery(
    ProjectsActionTypes.toggleUserEnableToEditOutline[AsyncActionStatus.PENDING],
    toggleUserEnableToEditOutline,
  ),
  takeEvery(
    ProjectsActionTypes.toggleUserEnableToDownloadOutline[AsyncActionStatus.PENDING],
    toggleUserEnableToDownloadOutline,
  ),
  takeEvery(
    ProjectsActionTypes.getProjectsDataForExcel[AsyncActionStatus.PENDING],
    getProjectsDataForExcel,
  ),
  takeEvery(
    ProjectsActionTypes.customerReviewedUpgrades[AsyncActionStatus.PENDING],
    customerReviewedUpgrades,
  ),
  takeEvery(
    ProjectsActionTypes.getDataFromHubspot[AsyncActionStatus.PENDING],
    getDataFromHubspot,
  ),
  takeEvery(
    ProjectsActionTypes.editorSeenRequestForUpgrade[AsyncActionStatus.PENDING],
    editorSeenRequestForUpgrade,
  ),
  takeEvery(
    ProjectsActionTypes.getBookSampleAndResourcesByUser[AsyncActionStatus.PENDING],
    getBookSampleAndResourcesByUser,
  ),
  takeEvery(
    ProjectsActionTypes.getBookSampleAndResourcesByProjectId[AsyncActionStatus.PENDING],
    getBookSampleAndResourcesByProjectId,
  ),
  takeEvery(
    ProjectsActionTypes.getProjectAndBookData[AsyncActionStatus.PENDING],
    getProjectAndBookData,
  ),
  takeEvery(
    ProjectsActionTypes.cancelUpgradeRequest[AsyncActionStatus.PENDING],
    cancelUpgradeRequest,
  ),
  takeEvery(
    ProjectsActionTypes.completeUpgradeRequest[AsyncActionStatus.PENDING],
    completeUpgradeRequest,
  ),
  takeEvery(
    ProjectsActionTypes.getNextStageForProject[AsyncActionStatus.PENDING],
    getNextStageForProject,
  ),
];
